import { useMemo } from 'react'
import { ExternalLink, FileText } from 'lucide-react'

import {
  type Documento,
  documentosDoElemento,
  etiqueta,
  ligacaoAbrivel,
} from '@viewer/lib/ifcDocuments'

interface Props {
  modelId: string
  localId: number
}

/** Os documentos que o modelo associa ao elemento seleccionado.
 *
 *  Vive à parte do PropertiesPanel, que já vai em duas mil linhas, e
 *  faz a sua própria leitura: o índice é por modelo e fica em cache,
 *  por isso isto não custa uma varredura por clique.
 *
 *  O que se mostra é o que o IFC diz, e nada mais. Não vamos buscar o
 *  ficheiro, não o guardamos, e não fingimos que sabemos abri-lo: um
 *  caminho de rede aparece como texto, para se copiar, porque uma
 *  ligação que não abre é pior do que texto nenhum. */
export default function DocumentosDoElemento({ modelId, localId }: Props) {
  const docs = useMemo(
    () => documentosDoElemento(modelId, localId),
    [modelId, localId],
  )

  if (docs.length === 0) {
    return (
      <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
        Sem documentos associados a este elemento.
      </div>
    )
  }

  return (
    <ul className="flex flex-col gap-1.5 text-[11px]">
      {docs.map((d, i) => (
        <li
          key={`${etiqueta(d)}-${i}`}
          className="rounded border border-[var(--color-border)] px-2 py-1.5"
        >
          <Cabecalho doc={d} />
          {d.viaTipo && (
            <p
              className="mt-0.5 text-[10px] text-[var(--color-accent)]"
              title="O documento está associado ao type no IFC, não a esta instância. Vale para todas as instâncias do type."
            >
              Do type {d.viaTipo}
            </p>
          )}
          {d.descricao && (
            <p className="mt-0.5 text-[var(--color-text-muted)]">
              {d.descricao}
            </p>
          )}
          <Localizacao doc={d} />
          <Estado doc={d} />
        </li>
      ))}
    </ul>
  )
}

function Cabecalho({ doc }: { doc: Documento }) {
  return (
    <div className="flex items-start gap-1.5">
      <FileText
        size={12}
        className="mt-0.5 shrink-0 text-[var(--color-text-muted)]"
        aria-hidden
      />
      <span className="min-w-0 break-words font-medium text-[var(--color-text)]">
        {etiqueta(doc)}
      </span>
    </div>
  )
}

function Localizacao({ doc }: { doc: Documento }) {
  if (!doc.localizacao) return null
  const ligacao = ligacaoAbrivel(doc.localizacao)
  if (ligacao) {
    return (
      <a
        href={ligacao}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 flex items-center gap-1 break-all text-[var(--color-accent)] hover:underline"
        title="Abrir numa aba nova"
      >
        <ExternalLink size={11} className="shrink-0" aria-hidden />
        {doc.localizacao}
      </a>
    )
  }
  return (
    <p
      className="mt-1 break-all font-mono text-[10px] text-[var(--color-text-dim)]"
      title="O modelo diz que o documento está aqui. Não é um endereço que o browser possa abrir, por isso fica como texto para copiares."
    >
      {doc.localizacao}
    </p>
  )
}

function Estado({ doc }: { doc: Documento }) {
  if (!doc.revisao && !doc.estado) return null
  return (
    <p className="mt-1 text-[10px] text-[var(--color-text-dim)]">
      {doc.revisao && <>Revisão {doc.revisao}</>}
      {doc.revisao && doc.estado && ' · '}
      {doc.estado}
    </p>
  )
}
